import React from 'react'
import { useSelector } from 'react-redux'
import slugify from 'slugify'
import { graphql } from 'gatsby'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'

import Seo from '../components/seo'
import Layout from '../components/layout'
import OfferLayout from '../components/layouts/offer'

import pageAlternatesUrls from '../utils/pageAlternatesUrls'

function Offer({data, pageContext}) {
  const mainContent = data.mainContent
  const lang = useSelector(state => state.lang)
  const urls = pageAlternatesUrls(mainContent, data.alternateContent, 'title', `/solutions/${slugify(lang === 'fr' ? mainContent.solution.title : data.alternateContent.solution.title, {remove: /[*+~.,()'"!?:@,]/g}).toLowerCase()}/`, `/en/solutions/${slugify(lang === 'fr' ? data.alternateContent.solution.title : mainContent.solution.title, {remove: /[*+~.,()'"!?:@,]/g}).toLowerCase()}/`, pageContext.locale)

  return (
    <Layout 
      frUrl={urls.frUrl}
      enUrl={urls.enUrl} 
      initLocale={pageContext.locale}
      mainTag={mainContent.mainTag.title}
      headerType="text-white"
      isSolution={true}
    >
      <Seo
        frUrl={urls.frUrl}
        enUrl={urls.enUrl}
        page={{
          title: mainContent.seoTitle ? mainContent.seoTitle : mainContent.title,
          description: mainContent.seoDescription ? mainContent.seoDescription.seoDescription : documentToPlainTextString(JSON.parse(mainContent.chapo.raw)),
          image: (mainContent.mainImage) ? mainContent.mainImage.file.file.url : null,
          path: (pageContext.locale === 'fr') ? urls.frUrl : urls.enUrl
        }}
      />

      <OfferLayout currentUrl={(pageContext.locale === 'fr') ? urls.frUrl : urls.enUrl} data={mainContent} furtherContent={data.furtherContent} />
    </Layout>
  );
}

export const pageQuery = graphql`
  query SingleOffer($contentful_id: String!, $locale: String!, $mainTag: String!) {
    mainContent: contentfulOffer(contentful_id: { eq: $contentful_id }, node_locale: { eq: $locale }) {
      internal {
        type
      }
      seoTitle
      seoDescription {
        seoDescription
      }
      mainImage {
        altImage
        file {
          file {
            url
          }
        }
      }
      logo {
        altImage
        file {
          file {
            url
          }
        }
      }
      solution {
        solution
        title
      }
      mainTag {
        title
      }
      breadcrumb {
        pageLink {
          ... on Node {
            ... on ContentfulEvent {
              title
              node_locale
              internal {
                type
              }
            } 
            ... on ContentfulNews {
              title
              node_locale
              internal {
                type
              }
            }
            
            ... on ContentfulHomepageSolutions {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulContact {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulSimulator {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulHub {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulPage {
              title
              slug
              customType
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulNewsroomList {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulSolution {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulOffer {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulLanding {
              title
              slug
              node_locale
              internal {
                type
              }
            }
          }
        }
        label
        customLink
        assetLink {
          file {
            url
          }
        }
      }
      breadcrumbCurrentLabel
      title
      titleLine1
      titleLine2
      chapo {
        raw
      }
      node_locale
      challenges {
        title
        text {
          text
        }
        picture {
          altImage
          file {
            file {
              url
            }
          }
        }
      }
      mapContacts {
        internal {
          type
        }
        title
        subtitle
        contactZone1 {
          title
          content {
            raw
          }
        }
        contactZone2 {
          title
          content {
            raw
          }
        }
        contactZone3 {
          title
          content {
            raw
          }
        }
        text {
          raw
          references {
            __typename
            ... on ContentfulNewsroomList {
              contentful_id
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulNews {
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulSolution {
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulLanding {
              contentful_id
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulOffer {
              solution {
                title
              }
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulEvent {
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulContact {
              title
              contentful_id
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulHub {
              title
              slug
              contentful_id
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulHomepageSolutions {
              title
              contentful_id
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulPage {
              contentful_id
              title
              slug
              customType
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulAsset {
              contentful_id
              file {
                url
              }
            }
          }
        }
      }
      slices {
        ... on Node {
          ... on ContentfulRates {
            internal {
              type
            }
            title
            items {
              isFull
              title
              rate
              rateUnit
              mention
              content {
                raw
              }
              cta {
                pageLink {
                  ... on Node {
                    ... on ContentfulEvent {
                      title
                      node_locale
                      internal {
                        type
                      }
                    } 
                    ... on ContentfulNews {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulSolution {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulLanding {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulOffer {
                      solution {
                        title
                      }
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulHomepageSolutions {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulContact {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulSimulator {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulHub {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulPage {
                      title
                      slug
                      customType
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulNewsroomList {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                  }
                }
                label
                customLink
                assetLink {
                  file {
                    url
                  }
                }
              }
            }
          }
          ... on ContentfulFullText {
            internal {
              type
            }
            displayTitle
            title
            switchDecoPlacement
            cta {
              pageLink {
                ... on Node {
                  ... on ContentfulEvent {
                    title
                    node_locale
                    internal {
                      type
                    }
                  } 
                  ... on ContentfulNews {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSolution {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLanding {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulOffer {
                    solution {
                      title
                    }
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepageSolutions {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNewsroomList {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                }
              }
              label
              customLink
              assetLink {
                file {
                  url
                }
              }
            }
            content {
              raw
              references {
                __typename
                ... on ContentfulStyledText {
                  contentful_id
                  text
                  type
                }
                ... on ContentfulTestimony {
                  contentful_id
                  image {
                    altImage
                    file {
                      url
                    }
                  }
                  themeSolutions
                  imageShape
                  anchorTitle
                  content {
                    content
                  }
                  author
                  job
                  theme
                }
                ... on ContentfulVideo {
                  contentful_id
                  title
                  suptitle
                  youtubeId
                  youtubePlaylistId
                  cover {
                    file {
                      url
                    }
                  }
                }
                ... on ContentfulFrontImg {
                  contentful_id
                  file {
                    url
                  }
                  altImage
                  legend {
                    legend
                  }
                }
                ... on ContentfulNewsroomList {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulNews {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSolution {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulLanding {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulOffer {
                  solution {
                    title
                  }
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulEvent {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulContact {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSimulator {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHub {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHomepageSolutions {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulPage {
                  contentful_id
                  title
                  slug
                  customType
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulAsset {
                  contentful_id
                  file {
                    url
                  }
                }
              }
            }
            node_locale
          }
          ... on ContentfulImageTexteSection {
            internal {
              type
            }
            node_locale
            themeSolutions
            imageShape
            title
            content {
              raw
              references {
                __typename
                ... on ContentfulStyledText {
                  contentful_id
                  text
                  type
                }
                ... on ContentfulNewsroomList {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulNews {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSolution {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulLanding {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulOffer {
                  solution {
                    title
                  }
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulEvent {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulContact {
                  title
                  contentful_id
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSimulator {
                  title
                  slug
                  contentful_id
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHub {
                  title
                  slug
                  contentful_id
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHomepageSolutions {
                  title
                  contentful_id
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulPage {
                  contentful_id
                  title
                  slug
                  customType
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulAsset {
                  contentful_id
                  file {
                    url
                  }
                }
              }
            }
            proportions
            cta {
              pageLink {
                ... on Node {
                  ... on ContentfulEvent {
                    title
                    node_locale
                    internal {
                      type
                    }
                  } 
                  ... on ContentfulNews {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSolution {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLanding {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulOffer {
                    solution {
                      title
                    }
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepageSolutions {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNewsroomList {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                }
              }
              customLink
              label
              assetLink {
                file {
                  url
                }
              }
            }
            image {
              altImage
              file {
                file {
                  url
                }
              }
            }
            reversed
            theme
          }
          ... on ContentfulTestimony {
            internal {
              type
            }
            author
            job
            themeSolutions
            imageShape
            theme
            anchorTitle
            content {
              content
            }
            image {
              altImage
              file {
                file {
                  url
                }
              }
            }
          }
          ... on ContentfulSliderPartnersClients {
            internal {
              type
            }
            title
            items {
              logo {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
              link
              noFollow
            }
          }
          ... on ContentfulSlider {
            internal {
              type
            }
            title
            type
            chapo {
              chapo
            }
            items {
              ... on Node {
                ... on ContentfulFrontImg {
                  altImage
                  file {
                    file {
                      url
                    }
                  }
                  legend:childContentfulFrontImgLegendTextNode {
                    legend
                  }
                }
                ... on ContentfulShortItem {
                  title
                  text {
                    text
                  }
                }
              }
            }
          }
        }
      }
      otherOffers {
        internal {
          type
        }
        node_locale
        title
        solution {
          solution
          title
        }
        mainImage {
          altImage
          file {
            file {
              url
            }
          }
        }
      }
    }

    alternateContent: contentfulOffer(contentful_id: { eq: $contentful_id }, node_locale: { ne: $locale }, title: {ne: null}) {
      title
      node_locale
      solution {
        title
      }
    }

    furtherContent: allContentfulPage(filter: {distributionChannel: {elemMatch: {title: {eq: "Terega solutions"}}}, customType: {eq: "Edito"}, contentful_id: { ne: $contentful_id }, node_locale: {eq: $locale},  title: {ne: null}, mainTag: {title: {eq: $mainTag}}}, limit: 3, sort: {fields: createdAt, order: DESC}) {
      nodes {
        title
        slug
        internal {
          type
        }
        mainTag {
          title
        }
        mainImage {
          altImage
          file {
            file {
              url
            }
          }
        }
        chapo {
          raw
          references {
            __typename
            ... on ContentfulStyledText {
              contentful_id
              text
              type
            }
            ... on ContentfulNewsroomList {
              contentful_id
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulNews {
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulSolution {
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulLanding {
              contentful_id
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulOffer {
              solution {
                title
              }
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulEvent {
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulContact {
              contentful_id
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulSimulator {
              contentful_id
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulHub {
              contentful_id
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulHomepageSolutions {
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulPage {
              contentful_id
              title
              slug
              customType
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulAsset {
              contentful_id
              file {
                url
              }
            }
          }
        }
        node_locale
      }
    }
  }
`;

export default Offer